import React, { useEffect, useState, useCallback } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { Plus } from "lucide-react";
import { STATUSES, blankPost } from "./social/_shared";
import OauthAccountsCard from "./social/OauthAccountsCard";
import PostsGrid from "./social/PostsGrid";
import PostEditorModal from "./social/PostEditorModal";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../hooks/useFieldErrors";
import { requiredField } from "../lib/validation";
import LoadFailure from "../components/ui/LoadFailure";
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { CAP } from "./entitlements/capabilities";

// Mirrors modules/social.py's PostIn (content: Field(..., min_length=1, max_length=4000))
// and create_post's 400 when status="scheduled" with no scheduled_at.
const POST_SCHEMA = z.object({
  content: requiredField(4000),
  status: z.string(),
  scheduled_at: z.string().optional(),
}).superRefine((val, ctx) => {
  if (val.status === "scheduled" && !val.scheduled_at) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduled_at"], message: "social.scheduledAtRequired" });
  }
});

/**
 * SocialAdmin — orchestration only.
 * Avant B18 : 401L (complexité élevée). Après : 175L + 4 sous-composants.
 */
export default function SocialAdmin() {
  const t = useT("admin");
  const tc = useT("common");
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const ent = useEntitlements();
  // AI drafting (text and image) is the AI assistant's capability, not Social's: its
  // controls exist only while that capability is usable. Scheduling a post for later is
  // `social.publishing`; its state decides whether "scheduled" can be picked at all.
  const aiAvailable = ent.granted(CAP.aiAssistant);
  const scheduling = ent.status(CAP.socialPublishing);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const [aiDraft, setAiDraft] = useState(null);
  const [genImagePrompt, setGenImagePrompt] = useState("");
  const [genImage, setGenImage] = useState(false);
  const [providers, setProviders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [publishingId, setPublishingId] = useState(null);
  const { fieldErrors, validate, clearFieldError, clearAllErrors } = useFieldErrors();

  const loadOauth = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([api.get("/social/oauth/providers"), api.get("/social/accounts")]);
      setProviders(p.data);
      setAccounts(a.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("[social-admin] oauth load skipped:", e?.message);
    }
  }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      const [p, s] = await Promise.all([
        api.get(`/social/posts?${params.toString()}`),
        api.get("/social/stats"),
      ]);
      setPosts(p.data);
      setStats(s.data);
    } catch (e) {
      // The route is plan-gated (RequireCapability): a refusal here is a failed read,
      // stated as the server states it, never re-read as "the module is off".
      setLoadError(e);
    }
  }, [filter]);

  useEffect(() => { load(); loadOauth(); }, [filter, load, loadOauth]);

  const connect = async (providerId) => {
    try {
      // A POST: starting a connection writes the flow's state (and, in demo mode, the
      // account itself), so an operator's start is audited like any other change.
      const { data } = await api.post(`/social/oauth/connect/${providerId}`);
      if (data.demo_mode) {
        toast.info(data.message || t("social.demoAccount"));
        loadOauth();
      } else {
        window.location.href = data.authorize_url;
      }
    } catch (e) {
      toast.error(extractApiError(e, t("social.oauthError")));
    }
  };

  const disconnect = async (id) => {
    if (!window.confirm(t("social.confirmDisconnect"))) return;
    try {
      await api.delete(`/social/accounts/${id}`);
      toast.success(t("social.accountDisconnected"));
      loadOauth();
    } catch (e) {
      toast.error(extractApiError(e, t("social.error")));
    }
  };

  const publishNow = async (postId) => {
    setPublishingId(postId);
    try {
      const { data } = await api.post(`/social/posts/${postId}/publish`, { account_ids: [] });
      const ok = (data.results || []).filter((r) => r.ok).length;
      const total = (data.results || []).length;
      const demo = (data.results || []).some((r) => r.demo_mode);
      toast.success(t("social.publishedOn", { ok, total, demo: demo ? t("social.demoSuffix") : "" }));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("social.publishError")));
    } finally {
      setPublishingId(null);
    }
  };

  const save = async () => {
    if (validate(POST_SCHEMA, editing)) { toast.error(tc("validation.fixHighlighted")); return; }
    try {
      const payload = { ...editing, hashtags: (editing.hashtags || []).filter(Boolean) };
      if (editing.id) await api.put(`/social/posts/${editing.id}`, payload);
      else await api.post("/social/posts", payload);
      toast.success(t("social.postSaved"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("social.error")));
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("social.confirmDeletePost"))) return;
    try {
      await api.delete(`/social/posts/${id}`);
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("social.error")));
    }
  };

  const openEditor = (post) => { clearAllErrors(); setEditing(post); };

  const generateText = async () => {
    if (!genTopic.trim()) return toast.error(t("social.topicRequired"));
    setGenerating(true);
    try {
      const { data } = await api.post("/social/ai/generate-text", {
        topic: genTopic, platforms: editing.platforms, include_hashtags: true,
      });
      // Garde-fou IA : on NE pousse PAS le texte directement dans le post.
      // Il reste en brouillon jusqu'à approbation explicite.
      setAiDraft({ text: data.text, hashtags: data.hashtags });
      toast.success(t("social.draftGenerated"));
    } catch (e) {
      toast.error(extractApiError(e, t("social.aiError")));
    } finally {
      setGenerating(false);
    }
  };

  const approveDraft = () => {
    if (!aiDraft) return;
    setEditing({ ...editing, content: aiDraft.text, hashtags: aiDraft.hashtags });
    setAiDraft(null);
    toast.success(t("social.draftApproved"));
  };

  const discardDraft = () => setAiDraft(null);

  const generateImage = async () => {
    if (!genImagePrompt.trim()) return toast.error(t("social.describeImage"));
    setGenImage(true);
    try {
      const { data } = await api.post("/social/ai/generate-image", { prompt: genImagePrompt });
      setEditing({ ...editing, image_url: data.image_url });
      toast.success(t("social.imageGenerated"));
    } catch (e) {
      toast.error(extractApiError(e, t("social.aiError")));
    } finally {
      setGenImage(false);
    }
  };

  return (
    <div data-testid="social-admin" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl text-eh-ink">{t("social.title")}</h1>
          <p className="text-eh-muted text-sm mt-1">
            {t("social.subtitle", { total: stats?.total ?? 0, scheduled: stats?.scheduled ?? 0, drafts: stats?.drafts ?? 0 })}
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded-md px-3 h-10 text-sm"
            data-testid="social-filter"
          >
            <option value="">{t("social.all")}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{t(`social.statusOpt.${s}`)}</option>)}
          </select>
          <Button data-testid="social-new" onClick={() => openEditor(blankPost())} className="bg-eh-ink text-eh-bg">
            <Plus className="w-4 h-4 mr-1" /> {t("social.newPost")}
          </Button>
        </div>
      </div>

      <LoadFailure
        message={loadError ? extractApiError(loadError, tc("errors.loadFailed"), tc) : ""}
        onRetry={load}
        retryLabel={tc("actions.retry")}
        testId="social-admin-load-error"
        retryTestId="social-admin-retry"
      />

      <OauthAccountsCard accounts={accounts} providers={providers} onConnect={connect} onDisconnect={disconnect} t={t} />

      <PostsGrid
        posts={posts}
        accounts={accounts}
        publishingId={publishingId}
        onEdit={openEditor}
        onPublish={publishNow}
        onDelete={del}
        t={t}
      />

      <PostEditorModal
        editing={editing}
        setEditing={setEditing}
        onSave={save}
        onClose={() => setEditing(null)}
        genTopic={genTopic} setGenTopic={setGenTopic} generating={generating} onGenerateText={generateText}
        aiDraft={aiDraft} onApproveDraft={approveDraft} onDiscardDraft={discardDraft}
        genImagePrompt={genImagePrompt} setGenImagePrompt={setGenImagePrompt} genImage={genImage} onGenerateImage={generateImage}
        aiAvailable={aiAvailable}
        scheduling={scheduling}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
        tc={tc}
        t={t}
      />
    </div>
  );
}
